import ObjectComparer from 'common/services/util/-RFC.ObjectComparer';
import ActivationCodeNotifications from 'common/notifications/-RFC.activationCodeNotifications';

let _activationCodes = [];
const observableActivationCodes = new Rx.BehaviorSubject(_activationCodes);
const { primeAppIds } = CWCONFIG || [];

/**
     * @ngdoc service
     * @name repositories.ActivationCodesRepository
     *
     * @description
     * This class is used to get activation codes objects.
     */
const ActivationCodesRepository = {

    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#getAll
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to retrieves all activation codes from repository.
         *
         * @returns {Array} activationCodes all activation codes from repository
         */
    getAll() {

        return _activationCodes;

    },

    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#addOrUpdate
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to add/update an activation code to repository.
         *
         * @param {object} activationCode An activation code object.
         */
    addOrUpdate(activationCode) {

        const indexOfActivationCode = _activationCodes
            .findIndex(_activationCode => String(_activationCode.id) === String(activationCode.id));

        if (indexOfActivationCode === -1) {

            this.add(activationCode);

        } else {

            this.update(activationCode);

        }

    },

    /**
        * @ngdoc function
        * @name repositories.ActivationCodesRepository#add
        * @methodOf repositories.ActivationCodesRepository
        * @function
        *
        * @description
        * This function can be used to add a new activation code to repository.
        *
        * @param {object} activationCode An activation code object.
        */
    add(activationCode) {

        const indexOfActivationCode = _activationCodes
            .findIndex(_activationCode => String(_activationCode.id) === String(activationCode.id));

        if (indexOfActivationCode === -1) {

            ActivationCodeNotifications.processCreate(activationCode);
            _activationCodes.push(activationCode);
            observableActivationCodes.onNext(_activationCodes);

        }

    },

    /**
        * @ngdoc function
        * @name repositories.ActivationCodesRepository#update
        * @methodOf repositories.ActivationCodesRepository
        * @function
        *
        * @description
        * This function can be used to update an activation code to repository.
        *
        * @param {object} activationCode An activation code object.
        */
    update(activationCode) {

        const indexOfActivationCode = _activationCodes
            .findIndex(_activationCode => String(_activationCode.id) === String(activationCode.id));

        if (indexOfActivationCode >= 0) {

            const flattenDiff = ObjectComparer
                .getFlattenDiff(_activationCodes[indexOfActivationCode], activationCode);

            ActivationCodeNotifications
                .processUpdate(
                    flattenDiff,
                    _activationCodes[indexOfActivationCode],
                    activationCode,
                );

            if (!angular.equals(flattenDiff, {})) {

                _activationCodes[indexOfActivationCode] = activationCode;
                observableActivationCodes.onNext(_activationCodes);

            }

        }

    },


    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#replace
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to replace all stored activation codes with provided data.
         *
         * @param {Array} data An array with new activation codes.
         */
    replace(data) {

        _activationCodes = data;
        observableActivationCodes.onNext(_activationCodes);

    },

    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#getById
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to retrieves an activation codes from repository.
         *
         * @returns {Object} activationCode an activation code from repository
         */
    getById(id) {

        return _activationCodes
            .find(activationCode => (String(activationCode.id) === String(id)));

    },

    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#getByAppId
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to retrieves an activation codes from repository.
         *
         * @returns {Object} activationCode an activation code from repository
         */
    getByAppId(appId) {

        return _activationCodes.find(activationCode =>
            (String(activationCode.attributes.app) === String(appId)));

    },

    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#getObservable
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to get an observable object for activation codes repository.
         * @returns {Object} an observable object for activation codes repository.
         */
    getObservable() {

        return observableActivationCodes;

    },
    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#hasTPLinkLicence
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to check whether repo contains a TP-link licence(Prime aps30).
         * @returns {Boolean}.
         */
    hasTPLinkLicence() {

        return _activationCodes.findIndex(activationCode => activationCode.relationships.app.data.id === 'aps30') > -1;

    },
    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#hasActivePrime
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * check whether repo contains a active/subscription ON Prime activation code.
         * @returns {Boolean}.
         */
    hasActivePrime() {

        const userHavePrime = _activationCodes.findIndex(activationCode =>
            primeAppIds.indexOf(activationCode.relationships.app.data.id) > -1 &&
            activationCode.attributes.subscription === 1) > -1;

        return userHavePrime;

    },

    /**
         * @ngdoc function
         * @name repositories.ActivationCodesRepository#deleteActivationCodeById
         * @methodOf repositories.ActivationCodesRepository
         * @function
         *
         * @description
         * This function can be used to delete a specific activation code
         * from repository based on id.
         * @returns {Object}.
         */
    deleteActivationCodeById(id) {

        _activationCodes = _activationCodes.filter(activationCode => activationCode.id !== id);
        observableActivationCodes.onNext(_activationCodes);

    },

};

export default ActivationCodesRepository;



// WEBPACK FOOTER //
// ./scripts/common/repositories/-RFC.activationCodesRepository.js